import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { formatPhoneNumber, formatDate } from "../../utils";
import { API_BASE_URL } from "../../constants";
import styles from "./MergeModal.module.css";

/**
 * MergeModal - Component for merging two user accounts
 */
const MergeModal = React.memo(
  ({ isOpen, sourceUser, users, onClose, onMergeComplete }) => {
    const [targetUserId, setTargetUserId] = useState("");
    const [targetUser, setTargetUser] = useState(null);
    const [saveMessage, setSaveMessage] = useState("");
    const [saveMessageType, setSaveMessageType] = useState("");
    const [isMerging, setIsMerging] = useState(false);

    // Reset state when modal opens/closes or source user changes
    useEffect(() => {
      if (isOpen && sourceUser) {
        setTargetUserId("");
        setTargetUser(null);
        setSaveMessage("");
        setSaveMessageType("");
        setIsMerging(false);
      }
    }, [isOpen, sourceUser]);

    // Filter out source user from selectable users and sort by name
    const selectableUsers = users
      ? users
          .filter((user) => user.id !== sourceUser?.id)
          .sort((a, b) => {
            const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
            const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
            return nameA.localeCompare(nameB);
          })
      : [];

    // Update target user when selection changes
    const handleTargetUserChange = useCallback(
      (e) => {
        const selectedId = e.target.value;
        setTargetUserId(selectedId);

        if (selectedId) {
          const selected = users.find((u) => u.id === selectedId);
          setTargetUser(selected || null);
        } else {
          setTargetUser(null);
        }
      },
      [users]
    );

    // Merge users function
    const handleMerge = useCallback(async () => {
      if (!sourceUser || !targetUser || isMerging) return;

      if (
        !window.confirm(
          `Are you sure you want to merge ${sourceUser.firstName} ${sourceUser.lastName} into ${targetUser.firstName} ${targetUser.lastName}? This action cannot be undone.`
        )
      ) {
        return;
      }

      setIsMerging(true);
      setSaveMessage("Merging users...");
      setSaveMessageType("info");

      try {
        // Get authentication header based on stored token
        const token = localStorage.getItem("token");
        let headers = { "Content-Type": "application/json" };

        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        }

        const response = await fetch(`${API_BASE_URL}/users/merge`, {
          method: "POST",
          headers,
          body: JSON.stringify({
            sourceUserId: sourceUser.id,
            targetUserId: targetUser.id,
          }),
        });

        if (!response.ok) {
          throw new Error(`Error: ${response.status}`);
        }

        const result = await response.json();
        setSaveMessage("Users merged successfully!");
        setSaveMessageType("success");

        // Notify parent component about the merge
        if (onMergeComplete) {
          onMergeComplete(result.mergedUser);
        }

        // Close the modal after a short delay
        setTimeout(() => {
          onClose();
        }, 1500);
      } catch (error) {
        console.error("Failed to merge users:", error);
        setSaveMessage(`Failed to merge users: ${error.message}`);
        setSaveMessageType("error");
        setIsMerging(false);
      }
    }, [sourceUser, targetUser, isMerging, onClose, onMergeComplete]);

    // Close the modal
    const handleClose = useCallback(() => {
      if (!isMerging && onClose) {
        onClose();
      }
    }, [isMerging, onClose]);

    // Helper function to render user property
    const renderProperty = (label, value) => (
      <div className={styles.propertyGroup}>
        <div className={styles.propertyName}>{label}</div>
        <div className={styles.propertyValue}>{value || "Not set"}</div>
      </div>
    );

    // If not open, don't render anything
    if (!isOpen || !sourceUser) {
      return null;
    }

    return (
      <div className={styles.mergeModalOverlay}>
        <div className={styles.mergeModal}>
          <div className={styles.mergeModalHeader}>
            <h2>Merge Users</h2>
            <button
              className={styles.closeButton}
              onClick={handleClose}
              disabled={isMerging}
              aria-label="Close"
            >
              ×
            </button>
          </div>

          <div className={styles.mergeModalBody}>
            <div className={styles.description}>
              Merging combines two user accounts. All data from the source user
              will be transferred to the target user, and the source user will
              be deleted.
            </div>

            <div className={styles.userCard + " " + styles.primary}>
              <h3>Source User (will be deleted)</h3>
              {renderProperty("ID", sourceUser.id)}
              {renderProperty(
                "Name",
                `${sourceUser.firstName} ${sourceUser.lastName}`
              )}
              {renderProperty("Email", sourceUser.email)}
              {renderProperty("Phone", formatPhoneNumber(sourceUser.phone))}
              {renderProperty("Role", sourceUser.role)}
              {renderProperty("Status", sourceUser.status)}
              {renderProperty("Created", formatDate(sourceUser.createdAt))}
            </div>

            <label htmlFor="targetUser" className={styles.label}>
              Select Target User (will receive data)
            </label>
            <select
              id="targetUser"
              className={styles.select}
              value={targetUserId}
              onChange={handleTargetUserChange}
              disabled={isMerging}
            >
              <option value="">-- Select a user --</option>
              {selectableUsers.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.firstName} {user.lastName} ({user.email})
                </option>
              ))}
            </select>

            {targetUser && (
              <div className={styles.userCard + " " + styles.secondary}>
                <h3>Target User (will be kept)</h3>
                {renderProperty("ID", targetUser.id)}
                {renderProperty(
                  "Name",
                  `${targetUser.firstName} ${targetUser.lastName}`
                )}
                {renderProperty("Email", targetUser.email)}
                {renderProperty("Phone", formatPhoneNumber(targetUser.phone))}
                {renderProperty("Role", targetUser.role)}
                {renderProperty("Status", targetUser.status)}
                {renderProperty("Created", formatDate(targetUser.createdAt))}
              </div>
            )}

            <div className={styles.note}>
              <strong>Note:</strong> This action cannot be undone. The source
              user will be permanently deleted after the merge is complete.
            </div>

            {saveMessage && (
              <div
                className={`${styles.saveMessage} ${styles[saveMessageType]}`}
              >
                {saveMessage}
              </div>
            )}
          </div>

          <div className={styles.mergeModalFooter}>
            <button
              className={`${styles.cancelBtn} btn`}
              onClick={handleClose}
              disabled={isMerging}
            >
              Cancel
            </button>

            <button
              className={`${styles.mergeBtn} btn`}
              onClick={handleMerge}
              disabled={isMerging || !targetUser}
            >
              {isMerging ? "Merging..." : "Merge Users"}
            </button>
          </div>
        </div>
      </div>
    );
  }
);

// PropTypes for component
MergeModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  sourceUser: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    role: PropTypes.string,
    status: PropTypes.string,
    createdAt: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date),
    ]),
    updatedAt: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date),
    ]),
  }),
  users: PropTypes.arrayOf(PropTypes.object).isRequired,
  onClose: PropTypes.func.isRequired,
  onMergeComplete: PropTypes.func,
};

// Default props
MergeModal.defaultProps = {
  isOpen: false,
  onMergeComplete: () => {},
};

// Display name for debugging
MergeModal.displayName = "MergeModal";

export default MergeModal;
